/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, LinearProgress } from '@mui/material';
import React from 'react';
import type { HeaderSize, Position, DraggableSize } from '@opengeoweb/shared';
import {
  AlertBanner,
  ToolContainerDraggable,
  calculateStartSize,
} from '@opengeoweb/shared';
import type { uiTypes } from '@opengeoweb/store';
import { AreaDrawingFilter } from './AreaDrawingFilter';
import type { AreaDrawingFilterProps } from './AreaDrawingFilter';
import { AreasDrawings } from './AreasDrawings';
import type { DrawingListItem } from '../../store/warningsDrawings/types';
import type { Area } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';

const DEFAULT_LOADER_SIZE = { width: 320, height: 542 };
const DEFAULT_LOADER_POSITION = { top: 120, left: 100 };
const DEFAULT_LOADER_MIN_SIZE = { width: 160, height: 300 };

export const areaErrorType = 'area';

export const AreaDrawingLoader: React.FC<
  {
    bounds?: string;
    title?: string;
    onClose: () => void;
    onMouseDown?: () => void;
    isOpen: boolean;
    order?: number;
    source?: uiTypes.Source;
    size?: DraggableSize;
    startPosition?: Position;
    headerSize?: HeaderSize;
    drawingListItems: DrawingListItem[];
    error?: uiTypes.UISetErrorPayload;
    isLoading?: boolean;
    onAddArea?: (drawingListItem: DrawingListItem) => void;
    onRemoveArea?: (drawingListItem: DrawingListItem) => void;
    onHoverDrawing?: (drawingListItem: DrawingListItem) => void;
    onHoverDrawingEnd?: () => void;
    selectedAreas?: Area[];
    isChangeAreaDisabled?: boolean;
    onRetryRequest?: (errorType: string) => void;
    shouldDisableRemoveWarning?: boolean;
  } & AreaDrawingFilterProps
> = ({
  bounds,
  onClose,
  title,
  isOpen,
  onMouseDown = (): void => {},
  order = 0,
  source = 'app',
  size = DEFAULT_LOADER_SIZE,
  startPosition = DEFAULT_LOADER_POSITION,
  headerSize = 'xs',
  drawingListItems,
  error,
  isLoading = false,
  filters,
  onClickFilterChip,
  searchQuery,
  onSearch,
  selectedAreas,
  onAddArea = (): void => {},
  onRemoveArea = (): void => {},
  onHoverDrawing = (): void => {},
  onHoverDrawingEnd = (): void => {},
  onRetryRequest = (): void => {},
  isChangeAreaDisabled = true,
  shouldDisableRemoveWarning = false,
}) => {
  const { t } = useWarningsTranslation();
  const minSize = DEFAULT_LOADER_MIN_SIZE;
  const startSizeCalc = calculateStartSize(minSize, size, startPosition);
  const [sizeInState, setSizeInState] =
    React.useState<DraggableSize>(startSizeCalc);

  const areaFilterRef = React.useRef(null!);
  return (
    <ToolContainerDraggable
      bounds={bounds}
      headerSize={headerSize}
      isOpen={isOpen}
      minHeight={minSize.height}
      minWidth={minSize.width}
      onClose={onClose}
      onDragEnd={(_position, dragSize): void => {
        if (dragSize !== sizeInState) {
          setSizeInState(dragSize as DraggableSize);
        }
      }}
      onMouseDown={onMouseDown}
      onResizeStop={(_event, _direction, node): void => {
        const { offsetWidth: width, offsetHeight: height } = node;
        setSizeInState({ width, height });
      }}
      order={order}
      source={source}
      startPosition={startPosition}
      startSize={sizeInState}
      sx={{
        footer: {
          backgroundColor: 'inherit',
          boxShadow: 'none',
        },
      }}
      title={title || t('area-drawing-loader-title')}
    >
      {!!isLoading && (
        <LinearProgress
          color="secondary"
          data-testid="loading-bar"
          sx={{ position: 'absolute', width: '100%', top: 0 }}
        />
      )}
      <Box sx={{ padding: 1, height: '100%' }}>
        {!!error && (
          <AlertBanner
            title={error.error}
            {...(error.type === (areaErrorType as uiTypes.DialogType) && {
              actionButtonProps: {
                title: t('error-title-retry'),
                onClick: (): void => onRetryRequest(error.type),
              },
            })}
          />
        )}
        <AreaDrawingFilter
          filters={filters}
          onClickFilterChip={onClickFilterChip}
          onSearch={onSearch}
          ref={areaFilterRef}
          searchQuery={searchQuery}
        />
        <AreasDrawings
          drawingListItems={drawingListItems}
          filterRef={areaFilterRef}
          hasError={!!error}
          isChangeAreaDisabled={isChangeAreaDisabled}
          isLoading={isLoading}
          onAddArea={onAddArea}
          onHoverDrawing={onHoverDrawing}
          onHoverDrawingEnd={onHoverDrawingEnd}
          onRemoveArea={onRemoveArea}
          selectedAreas={selectedAreas}
          shouldDisableRemoveWarning={shouldDisableRemoveWarning}
        />
      </Box>
    </ToolContainerDraggable>
  );
};
